import Section from '../components/Section';
import Button from '../components/Button';

const About = () => {
  return (
    <>
      {/* Page Header */}
      <section className="bg-summit-navy-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold">About Summit MCG</h1>
          <p className="text-xl text-gray-200 mt-4">
            Experienced automotive consultancy built on real dealership expertise
          </p>
        </div>
      </section>

      {/* Our Background */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Our Background</h2>
          <p className="text-summit-grey leading-relaxed mb-4">
            Summit Motor Consultancy Group Limited was established to address a clear gap in the automotive consultancy market: the need for practical, commercially-focused support delivered by professionals who have built and led successful dealership operations.
          </p>
          <p className="text-summit-grey leading-relaxed mb-4">
            Unlike traditional consultancies that focus on theory and frameworks, Summit MCG was founded on the principle of hands-on partnership. We work alongside dealer teams, not just advise from the sidelines.
          </p>
          <p className="text-summit-grey leading-relaxed">
            Our consultants have walked the dealership floor, managed teams through market challenges, and delivered commercial results in live trading environments. This real-world experience informs everything we do.
          </p>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Our Experience */}
      <Section background="grey">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://images.unsplash.com/photo-1643142314913-0cf633d9bbb5?w=800&q=80" 
              alt="Professional automotive showroom" 
              className="rounded-sm shadow-lg"
            />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Our Experience</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-summit-navy mb-2">Senior Leadership Background</h3>
                <p className="text-summit-grey">
                  Every consultant within Summit MCG has held senior management positions within franchised dealer groups. This includes roles such as General Manager, Group Operations Director, and Sales Director.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-summit-navy mb-2">Proven Track Record</h3>
                <p className="text-summit-grey">
                  Our team has collectively delivered measurable improvements across hundreds of dealerships, spanning volume and premium brands, in markets across the UK and internationally.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-summit-navy mb-2">Industry Recognition</h3>
                <p className="text-summit-grey">
                  Summit consultants are trusted by OEM partners and dealer groups alike for their professionalism, discretion, and ability to drive genuine performance improvement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Our Approach */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Our Approach</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border-l-4 border-summit-gold pl-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Partnership, Not Just Consultancy</h3>
              <p className="text-summit-grey">
                We embed ourselves within your operation. Our consultants work on-site, alongside your management team, ensuring knowledge transfer and sustainable capability building.
              </p>
            </div>
            <div className="border-l-4 border-summit-gold pl-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Commercial Focus</h3>
              <p className="text-summit-grey">
                Every programme is aligned to clear commercial objectives. We measure success through tangible outcomes: improved margins, increased sales, enhanced customer retention.
              </p>
            </div>
            <div className="border-l-4 border-summit-gold pl-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Practical Implementation</h3>
              <p className="text-summit-grey">
                We don't provide theoretical frameworks and leave. Our consultants stay throughout implementation, ensuring that recommendations are actioned and results are delivered.
              </p>
            </div>
            <div className="border-l-4 border-summit-gold pl-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Discretion and Professionalism</h3>
              <p className="text-summit-grey">
                We understand the sensitive nature of dealership operations. All engagements are conducted with complete confidentiality and professionalism.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Why Clients Trust Us */}
      <Section background="grey">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-8 text-center">Why Clients Trust Us</h2>
          <div className="bg-white p-8 rounded-sm shadow-sm">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="text-summit-gold text-2xl mr-4">✓</div>
                <div>
                  <h3 className="font-semibold text-summit-navy mb-1">Real Dealership Experience</h3>
                  <p className="text-summit-grey">Not theoretical consultants. Experienced dealer professionals who have delivered results in live operations.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-summit-gold text-2xl mr-4">✓</div>
                <div>
                  <h3 className="font-semibold text-summit-navy mb-1">Measurable Outcomes</h3>
                  <p className="text-summit-grey">Clear KPIs, transparent reporting, and genuine accountability for performance improvement.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-summit-gold text-2xl mr-4">✓</div>
                <div>
                  <h3 className="font-semibold text-summit-navy mb-1">Long-Term Partnership</h3>
                  <p className="text-summit-grey">Many clients return for repeat programmes, reflecting trust in our approach and results.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-summit-gold text-2xl mr-4">✓</div>
                <div>
                  <h3 className="font-semibold text-summit-navy mb-1">Industry Credibility</h3>
                  <p className="text-summit-grey">Trusted by leading dealer groups and OEM partners across the UK automotive sector.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-4">Ready to Work Together?</h2>
          <p className="text-lg text-summit-grey mb-8">
            Discover how Summit MCG can help your dealership achieve peak performance.
          </p>
          <Button to="/contact">Start the Conversation</Button>
        </div>
      </Section>
    </>
  );
};

export default About;